import React from 'react';
import { formatCompactCurrency } from '../../../utils/fundingWorkspaceFormatters';
import PipelineStageStatusCard from './PipelineStageStatusCard';

const KPI = ({ label, value }) => (
  <article className="fg-summary-card">
    <span className="fg-summary-label">{label}</span>
    <strong className="fg-summary-value">{value}</strong>
  </article>
);

const AmountCard = ({ label, value, badge }) => (
  <article className="fg-summary-card fg-amount-card">
    <div>
      <span className="fg-summary-label">{label}</span>
      <strong className="fg-summary-value">{formatCompactCurrency(value)}</strong>
    </div>
    <span className="fg-card-badge">{badge}</span>
  </article>
);

const ApprovalGauge = ({ rate, approved, total }) => {
  const pct = Math.min(Math.max(rate || 0, 0), 100);
  return (
    <div className="fg-approval-wrap">
      <div
        className="fg-donut fg-donut-approval funding-approval-donut"
        style={{
          background: `conic-gradient(var(--fg-accent) ${pct * 3.6}deg, rgba(154, 186, 174, 0.22) 0deg)`,
        }}
      >
        <div className="funding-donut-center funding-donut-center--approval">
          <strong>{pct}<span>%</span></strong>
          <small>Approval rate</small>
        </div>
      </div>
      <span className="sr-only">{approved} of {total} approved</span>
    </div>
  );
};

const ProductBarRow = ({ label, value, max }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="fg-product-row">
      <span>{label}</span>
      <div className="fg-bar-track">
        <div className="fg-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <strong>{formatCompactCurrency(value)}</strong>
    </div>
  );
};

const FundingClientApplicationDashboard = ({ metrics }) => {
  if (!metrics) {
    return <p className="text-sm text-[var(--fg-muted)]">No application metrics yet.</p>;
  }

  const productItems = metrics.fundingByProduct || [];
  const productMax = Math.max(...productItems.map((i) => Number(i.value) || 0), 1);
  const pipelineStageData = metrics.pipelineStageData || [];
  const creditCardAmount = metrics.creditCardAmount ?? metrics.zeroPercentCreditCardAmount ?? 0;
  const businessFundingAmount = metrics.businessFundingAmount ?? metrics.businessLineOfCreditAmount ?? 0;

  return (
    <section className="fg-application-grid-stack" data-testid="application-dashboard">
      <div className="fg-kpi-grid">
        <KPI label="Total applications" value={metrics.totalApplications ?? 0} />
        <KPI label="Total approved" value={metrics.totalApproved ?? 0} />
        <KPI label="Total denied" value={metrics.totalDenied ?? 0} />
        <KPI label="Total approval" value={formatCompactCurrency(metrics.totalApprovalAmount)} />
        <KPI label="Best approval" value={formatCompactCurrency(metrics.bestApprovalAmount)} />
        <KPI label="Avg. EX used" value={metrics.avgExperianUsed ?? '—'} />
      </div>

      <div className="fg-three-column-grid">
        <AmountCard label="Personal funding amount" value={metrics.personalFundingAmount} badge="PF" />
        <AmountCard label="Credit card amount" value={creditCardAmount} badge="CC" />
        <AmountCard label="Business funding amount" value={businessFundingAmount} badge="BF" />
      </div>

      <div className="fg-three-column-grid">
        <PipelineStageStatusCard
          pipelineStageData={pipelineStageData}
          totalApplications={metrics.totalApplications ?? 0}
        />

        <article className="fg-chart-card fg-product-chart-card">
          <div className="fg-chart-head">
            <h3>Funding by Product</h3>
            <span className="fg-mini-pill">{formatCompactCurrency(metrics.totalApprovalAmount)} total</span>
          </div>
          <div className="fg-product-bars fg-product-bars--six">
            {productItems.length === 0 ? (
              <p className="text-sm text-[var(--fg-muted)]">No product totals yet.</p>
            ) : (
              productItems.map((item) => (
                <ProductBarRow key={item.label} label={item.label} value={item.value} max={productMax} />
              ))
            )}
          </div>
        </article>

        <article className="fg-chart-card">
          <div className="fg-chart-head">
            <h3>Approval Rate</h3>
            <span className="fg-mini-pill">{metrics.totalApproved ?? 0} / {metrics.totalApplications ?? 0} approved</span>
          </div>
          <ApprovalGauge
            rate={metrics.approvalRate}
            approved={metrics.totalApproved}
            total={metrics.totalApplications}
          />
        </article>
      </div>
    </section>
  );
};

export default FundingClientApplicationDashboard;
